import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Checkbox,
  Textarea,
  Button,
  IconButton,
  Text,
  useToast,
  Tooltip,
  Flex,
  Card,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import { AddIcon, CloseIcon, InfoIcon } from "@chakra-ui/icons";
import { nanoid } from "nanoid";
import axiosInstance from "../routes/axiosInstance";
import BackButton from "../components/BackButton";

const SPACE_KINDS = [
  { value: "CHAT", label: "Chat" },
  { value: "QNA", label: "Q&A" },
  { value: "POLL", label: "Poll" },
  { value: "FORM", label: "Form" },
  { value: "BROADCAST", label: "Broadcast" },
];

const KIND_DESCRIPTIONS = {
  chat: "Open chat—anyone with the link can join and send messages anonymously or authenticated.",
  qna: "Post one question; others reply in threads—great for Ask Me Anything sessions.",
  poll: "Create a question with options; participants vote anonymously.",
  form: "Build a survey with custom fields; collect structured responses.",
  broadcast: "One-way announcement; viewers see the message but cannot reply.",
};

const CreateSpacePage = () => {
  const { currentUser, checkUserAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // Track active tab
  // Basic settings
  const [kind, setKind] = useState("CHAT");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Expiry settings
  const [expiresEnabled, setExpiresEnabled] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");

  // Participant mode: anonymous, authenticated, or both
  const [participantMode, setParticipantMode] = useState("ANONYMOUS");

  // Incognito alias
  const [isIncognito, setIsIncognito] = useState(false);
  const [alias, setAlias] = useState("");

  // Type-specific fields
  const [question, setQuestion] = useState(""); // for Q&A
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [formFields, setFormFields] = useState([
    {
      fieldKey: "",
      label: "",
      fieldType: "text",
      choices: [{ id: nanoid(8), label: "" }],
    },
  ]);
  const [broadcastMsg, setBroadcastMsg] = useState("");

  // Handlers for dynamic lists
  const handleAddOption = () => setPollOptions([...pollOptions, ""]);
  const handleRemoveOption = (i) =>
    setPollOptions(pollOptions.filter((_, idx) => idx !== i));
  const handleOptionChange = (i, val) => {
    const arr = [...pollOptions];
    arr[i] = val;
    setPollOptions(arr);
  };

  const handleAddField = () =>
    setFormFields([
      ...formFields,
      {
        fieldKey: nanoid(8),
        label: "",
        fieldType: "text",
        choices: [{ id: nanoid(8), label: "" }],
        placeholder: "",
        required: false,
        order: formFields.length + 1,
      },
    ]);
  const handleRemoveField = (i) =>
    setFormFields(formFields.filter((_, idx) => idx !== i));
  const handleFieldChange = (i, key, val) => {
    const arr = [...formFields];
    arr[i][key] = val;
    setFormFields(arr);
  };
  const moveFieldUp = (i) => {
    if (i === 0) return;
    const arr = [...formFields];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setFormFields(arr.map((f, idx) => ({ ...f, order: idx + 1 })));
  };
  const moveFieldDown = (i) => {
    if (i === formFields.length - 1) return;
    const arr = [...formFields];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setFormFields(arr.map((f, idx) => ({ ...f, order: idx + 1 })));
  };

  // Create space API call
  const createSpaceHandler = async (payload) => {
    setLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };
    try {
      const newSpace = await axiosInstance.post("/api/spaces", payload, config);
      if (newSpace.status === 201) {
        toast({ title: "Space created!", status: "success" });
        setLoading(false);
        // Redirect to space list or newly created space
        navigate("/spaces");
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, status: "error" });
      setLoading(false);
      console.error(error.message);
      checkUserAuth(error.status);
    }
  };

  // Submit form to create space
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!title.trim()) {
      setActiveTab(0); // Switch to Overview tab
      toast({ title: "Title is required.", status: "warning" });
      return;
    }

    if (isIncognito && !alias.trim()) {
      setActiveTab(1); // Switch to Settings tab
      toast({ title: "Enter an alias for incognito mode.", status: "warning" });
      return;
    }

    const payload = { title: title.trim(), kind, participantMode, isIncognito };

    // Add description if provided
    if (description.trim()) {
      payload.description = description.trim();
    }

    // Handle expiry settings
    if (expiresEnabled && expiresAt) {
      payload.expiresAt = new Date(expiresAt).toISOString();
    }

    if (isIncognito) payload.alias = alias.trim();
    payload.metaData = {};
    switch (kind) {
      case "QNA":
        if (!question.trim()) {
          setActiveTab(0); // Switch to Overview tab
          toast({ title: "Enter a question.", status: "warning" });
          return;
        }
        payload.metaData.question = question.trim();
        break;
      case "POLL":
        if (!pollQuestion.trim()) {
          setActiveTab(0); // Switch to Overview tab
          toast({ title: "Enter poll question.", status: "warning" });
          return;
        }
        payload.metaData.question = pollQuestion.trim();
        // Generate unique ids for each poll option
        payload.metaData.options = pollOptions
          .filter((o) => o.trim())
          .map((label) => ({ id: nanoid(8), label: label.trim() }));
        break;
      case "FORM":
        payload.metaData.formSchema = formFields.map((f, idx) => ({
          fieldKey: f.fieldKey || nanoid(8),
          label: f.label,
          fieldType: f.fieldType,
          choices: ["select", "radio", "checkbox"].includes(f.fieldType)
            ? f.choices
                .filter((c) => c.label.trim())
                .map((c) => ({ id: c.id, label: c.label.trim() }))
            : [],
          placeholder:
            f.fieldType === "text" || f.fieldType === "email"
              ? f.placeholder || ""
              : undefined,
          required: !!f.required,
          order: f.order || idx + 1,
          scale: f.fieldType === "ratings" ? f.scale || 5 : undefined,
        }));
        break;
      case "BROADCAST":
        if (!broadcastMsg.trim()) {
          setActiveTab(0); // Switch to Overview tab
          toast({ title: "Enter announcement.", status: "warning" });
          return;
        }
        payload.metaData.content = broadcastMsg.trim();
        break;
      default:
        break;
    }
    createSpaceHandler(payload);
  };

  const handleAddChoice = (i) => {
    const arr = [...formFields];
    arr[i].choices.push({ id: nanoid(8), label: "" });
    setFormFields(arr);
  };
  const handleChoiceChange = (i, j, val) => {
    const arr = [...formFields];
    arr[i].choices[j].label = val;
    setFormFields(arr);
  };
  const handleRemoveChoice = (i, j) => {
    const arr = [...formFields];
    arr[i].choices.splice(j, 1);
    setFormFields(arr);
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <Flex align="center">
            <BackButton link="/spaces" />
            <Text fontSize="lg" fontWeight="600" color="gray.800" ml={3}>
              Create Space
            </Text>
          </Flex>
        </Card>

        {/* Content */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          <Box p={4}>
            <VStack spacing={4} align="stretch">
              <Text color="gray.600" fontSize="sm">
                Configure your space and participant settings.
              </Text>

              <Box as="form" onSubmit={handleSubmit}>
                <Tabs
                  colorScheme="teal"
                  index={activeTab}
                  onChange={setActiveTab}
                  variant="enclosed"
                >
                  <TabList bg="gray.50" borderRadius="lg" p={1}>
                    <Tab
                      borderRadius="md"
                      _selected={{
                        bg: "teal.500",
                        color: "white",
                        borderColor: "teal.500",
                      }}
                      _hover={{ bg: "teal.100" }}
                      fontWeight="600"
                      fontSize="sm"
                    >
                      Overview
                    </Tab>
                    <Tab
                      borderRadius="md"
                      _selected={{
                        bg: "teal.500",
                        color: "white",
                        borderColor: "teal.500",
                      }}
                      _hover={{ bg: "teal.100" }}
                      fontWeight="600"
                      fontSize="sm"
                    >
                      Settings
                    </Tab>
                  </TabList>

                  <TabPanels mt={4}>
                    {/* Overview Tab */}
                    <TabPanel px={0} py={2}>
                      <VStack spacing={4} align="stretch">
                        {/* 1. Space Type */}
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600">
                            Space Type
                          </FormLabel>
                          <Select
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            borderRadius="lg"
                            bg="gray.50"
                            _focus={{
                              borderColor: "teal.400",
                              boxShadow: "0 0 0 1px teal.400",
                              bg: "white",
                            }}
                          >
                            {SPACE_KINDS.map((k) => (
                              <option key={k.value} value={k.value}>
                                {k.label}
                              </option>
                            ))}
                          </Select>
                          <Text mt={1} fontSize="xs" color="gray.500">
                            {KIND_DESCRIPTIONS[kind]}
                          </Text>
                        </FormControl>
                        {/* 2. Title */}
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600">
                            Title
                          </FormLabel>
                          <Input
                            placeholder={
                              {
                                CHAT: "e.g. Team Standup",
                                QNA: "e.g. AMA with Jane Doe",
                                POLL: "e.g. Favorite Programming Language",
                                FORM: "e.g. Post-Meeting Survey",
                                BROADCAST: "e.g. Maintenance Announcement",
                              }[kind]
                            }
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            borderRadius="lg"
                            bg="gray.50"
                            _focus={{
                              borderColor: "teal.400",
                              boxShadow: "0 0 0 1px teal.400",
                              bg: "white",
                            }}
                          />
                        </FormControl>
                        {/* 3. Description */}
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600">
                            Description (optional)
                          </FormLabel>
                          <Textarea
                            placeholder="Enter a brief description of your space..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            resize="vertical"
                            borderRadius="lg"
                            bg="gray.50"
                            _focus={{
                              borderColor: "teal.400",
                              boxShadow: "0 0 0 1px teal.400",
                              bg: "white",
                            }}
                          />
                        </FormControl>{" "}
                        {/* Type-Specific Config */}
                        {kind === "QNA" && (
                          <FormControl>
                            <FormLabel>Question</FormLabel>
                            <Textarea
                              placeholder="Enter your question..."
                              value={question}
                              onChange={(e) => setQuestion(e.target.value)}
                            />
                          </FormControl>
                        )}
                        {kind === "POLL" && (
                          <Box borderWidth="1px" borderRadius="md" p={3}>
                            <FormControl mb={3}>
                              <FormLabel>Poll Question</FormLabel>
                              <Input
                                placeholder="Enter poll question..."
                                value={pollQuestion}
                                onChange={(e) =>
                                  setPollQuestion(e.target.value)
                                }
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Options</FormLabel>
                              <VStack align="stretch" spacing={2}>
                                {pollOptions.map((opt, i) => (
                                  <HStack key={i} spacing={2}>
                                    <Input
                                      flex={1}
                                      placeholder={`Option ${i + 1}`}
                                      value={opt}
                                      onChange={(e) =>
                                        handleOptionChange(i, e.target.value)
                                      }
                                    />
                                    {pollOptions.length > 2 && (
                                      <IconButton
                                        size="sm"
                                        icon={<CloseIcon />}
                                        onClick={() => handleRemoveOption(i)}
                                      />
                                    )}
                                  </HStack>
                                ))}
                                <Button
                                  leftIcon={<AddIcon />}
                                  size="sm"
                                  alignSelf="flex-start"
                                  onClick={handleAddOption}
                                >
                                  Add Option
                                </Button>
                              </VStack>
                            </FormControl>
                          </Box>
                        )}
                        {kind === "FORM" && (
                          <VStack align="stretch" spacing={3}>
                            {formFields.map((f, i) => (
                              <Box
                                key={f.fieldKey}
                                borderWidth="1px"
                                borderRadius="md"
                                p={3}
                              >
                                <HStack justify="space-between">
                                  <Text fontWeight="bold">Field {i + 1}</Text>
                                  <HStack>
                                    <IconButton
                                      size="sm"
                                      icon={<AddIcon />}
                                      aria-label="Move Up"
                                      onClick={() => moveFieldUp(i)}
                                      isDisabled={i === 0}
                                    />
                                    <IconButton
                                      size="sm"
                                      icon={<CloseIcon />}
                                      aria-label="Move Down"
                                      onClick={() => moveFieldDown(i)}
                                      isDisabled={i === formFields.length - 1}
                                    />
                                    {formFields.length > 1 && (
                                      <IconButton
                                        size="sm"
                                        icon={<CloseIcon />}
                                        onClick={() => handleRemoveField(i)}
                                      />
                                    )}
                                  </HStack>
                                </HStack>
                                <FormControl mt={2}>
                                  <FormLabel>Type</FormLabel>
                                  <Select
                                    value={f.fieldType}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        i,
                                        "fieldType",
                                        e.target.value,
                                      )
                                    }
                                  >
                                    {[
                                      { value: "text", label: "Text" },
                                      { value: "number", label: "Number" },
                                      { value: "email", label: "Email" },
                                      { value: "select", label: "Dropdown" },
                                      {
                                        value: "checkbox",
                                        label: "Checkboxes",
                                      },
                                      {
                                        value: "radio",
                                        label: "Multiple Choice",
                                      },
                                      { value: "ratings", label: "Ratings" },
                                    ].map((t) => (
                                      <option key={t.value} value={t.value}>
                                        {t.label}
                                      </option>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl mt={2}>
                                  <Input
                                    placeholder="Label"
                                    value={f.label}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        i,
                                        "label",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </FormControl>
                                {(f.fieldType === "text" ||
                                  f.fieldType === "email") && (
                                  <FormControl mt={2}>
                                    <Input
                                      placeholder="Placeholder"
                                      value={f.placeholder}
                                      onChange={(e) =>
                                        handleFieldChange(
                                          i,
                                          "placeholder",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </FormControl>
                                )}
                                <FormControl
                                  mt={2}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <HStack
                                    alignItems="center"
                                    justifyContent="flex-start"
                                  >
                                    <FormLabel mb={0} minW="70px">
                                      Required
                                    </FormLabel>
                                    <Switch
                                      colorScheme="green"
                                      isChecked={f.required}
                                      onChange={() =>
                                        handleFieldChange(
                                          i,
                                          "required",
                                          !f.required,
                                        )
                                      }
                                    />
                                  </HStack>
                                </FormControl>
                                {f.fieldType === "ratings" && (
                                  <FormControl mt={2}>
                                    <FormLabel>Rating Scale</FormLabel>
                                    <Select
                                      value={f.scale || 5}
                                      onChange={(e) =>
                                        handleFieldChange(
                                          i,
                                          "scale",
                                          Number(e.target.value),
                                        )
                                      }
                                    >
                                      {[3, 5, 7, 10].map((num) => (
                                        <option key={num} value={num}>
                                          {num}
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                                {(f.fieldType === "select" ||
                                  f.fieldType === "radio" ||
                                  f.fieldType === "checkbox") && (
                                  <FormControl mt={2}>
                                    <FormLabel>Choices</FormLabel>
                                    <Box
                                      borderWidth="1px"
                                      borderRadius="md"
                                      p={3}
                                      bg="gray.50"
                                    >
                                      <VStack spacing={2}>
                                        {f.choices.map((c, ci) => (
                                          <HStack key={c.id} spacing={2}>
                                            <Input
                                              flex={1}
                                              placeholder="Choice"
                                              value={c.label}
                                              onChange={(e) =>
                                                handleChoiceChange(
                                                  i,
                                                  ci,
                                                  e.target.value,
                                                )
                                              }
                                            />
                                            <IconButton
                                              size="sm"
                                              icon={<CloseIcon />}
                                              onClick={() =>
                                                handleRemoveChoice(i, ci)
                                              }
                                            />
                                          </HStack>
                                        ))}
                                        <Button
                                          leftIcon={<AddIcon />}
                                          size="sm"
                                          onClick={() => handleAddChoice(i)}
                                        >
                                          Add Choice
                                        </Button>
                                      </VStack>
                                    </Box>
                                  </FormControl>
                                )}
                              </Box>
                            ))}
                            <Button
                              leftIcon={<AddIcon />}
                              size="sm"
                              onClick={handleAddField}
                            >
                              Add Field
                            </Button>
                          </VStack>
                        )}
                        {kind === "BROADCAST" && (
                          <FormControl>
                            <FormLabel>Announcement</FormLabel>
                            <Textarea
                              placeholder="Enter announcement..."
                              value={broadcastMsg}
                              onChange={(e) => setBroadcastMsg(e.target.value)}
                            />
                          </FormControl>
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Settings Tab */}
                    <TabPanel px={0} py={2}>
                      <VStack align="stretch" spacing={4}>
                        {/* Participant Mode */}
                        <FormControl>
                          <HStack alignItems="center" spacing={2}>
                            <FormLabel mb={0} fontSize="sm" fontWeight="600">
                              Participant Mode
                            </FormLabel>
                            <Tooltip
                              label="Choose whether participants join anonymously or via login."
                              fontSize="sm"
                            >
                              <InfoIcon color="gray.500" />
                            </Tooltip>
                          </HStack>
                          <RadioGroup
                            onChange={setParticipantMode}
                            value={participantMode}
                          >
                            <HStack spacing={4} mt={2}>
                              <Radio value="ANONYMOUS" fontSize="sm">
                                Anonymous
                              </Radio>
                              <Radio value="AUTHENTICATED" fontSize="sm">
                                Authenticated
                              </Radio>
                            </HStack>
                          </RadioGroup>
                        </FormControl>

                        {/* Expiry Settings */}
                        <FormControl>
                          <HStack alignItems="center" spacing={2}>
                            <Checkbox
                              isChecked={expiresEnabled}
                              onChange={(e) =>
                                setExpiresEnabled(e.target.checked)
                              }
                              fontSize="sm"
                              fontWeight="600"
                            >
                              Add expiry time
                            </Checkbox>
                            <Tooltip
                              label="Set space expiration time."
                              fontSize="sm"
                            >
                              <InfoIcon color="gray.500" />
                            </Tooltip>
                          </HStack>
                          {expiresEnabled && (
                            <Box ml={6} mt={3}>
                              <VStack spacing={3} align="stretch">
                                <Text fontSize="sm" color="gray.600">
                                  Select expiry date & time:
                                </Text>
                                <Input
                                  type="datetime-local"
                                  value={expiresAt}
                                  onChange={(e) => setExpiresAt(e.target.value)}
                                  size="sm"
                                  borderRadius="lg"
                                />
                              </VStack>
                            </Box>
                          )}
                        </FormControl>

                        {/* Incognito Alias */}
                        <FormControl>
                          <HStack alignItems="center" spacing={2}>
                            <Checkbox
                              isChecked={isIncognito}
                              onChange={(e) => setIsIncognito(e.target.checked)}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              Incognito mode
                            </Checkbox>
                            <Tooltip
                              label="Use your custom mickname, real identity will be hidden."
                              fontSize="sm"
                            >
                              <InfoIcon color="gray.500" />
                            </Tooltip>
                          </HStack>
                          {isIncognito && (
                            <Box ml={6} mt={2}>
                              <Input
                                placeholder="Enter alias"
                                value={alias}
                                onChange={(e) => setAlias(e.target.value)}
                                borderRadius="lg"
                              />
                            </Box>
                          )}
                        </FormControl>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>

                {/* Submit Button */}
                <Button
                  type="submit"
                  colorScheme="teal"
                  size="lg"
                  mt={6}
                  isLoading={loading}
                  w="100%"
                  borderRadius="lg"
                >
                  Create Space
                </Button>
              </Box>
            </VStack>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default CreateSpacePage;
